import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import clsx from 'clsx';
import {
  Table as TableComponent,
  TableBody,
  TableContainer,
  TablePagination,
  TableRow,
  TableHead,
  TableCell,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogActions,
  Paper,
  Avatar,
  Grid,
  Checkbox,
} from '@material-ui/core';
import { AddBox, Edit, Check, Delete as DeleteIcon } from '@material-ui/icons';
import { CheckboxBlankCircleOutline, CheckboxMarkedCircle } from 'mdi-material-ui';
import { emptyFunction } from '@bforms-web/common';
import { useStyles } from './styles';
import SearchBar from '../SearchBar';
import PaperTitle from '../PaperTitle';
import { KeysLocalStorage } from '../../enums/keys-localstorage';

export type TableColumn = {
  avatar?: boolean;
  avatarFallback?: string;
  currency?: boolean;
  key: string;
  label: string;
  numeric?: boolean;
  width?: number | string;
  maxWidth?: number | string;
  render?: (value: any, item?: any) => JSX.Element | string;
};

export type TableProps = {
  addRoute?: string;
  canAdd?: boolean;
  canApprove?: boolean;
  canEdit?: boolean;
  canRemove?: boolean;
  canSearch?: boolean;
  canSelect?: boolean;
  canView?: boolean;
  columns: TableColumn[];
  count?: number;
  data: ReadonlyArray<{
    id: string;
    [key: string]: unknown;
  }>;
  editRoute?: string;
  elevation?: number;
  extraActions?: {
    icon: JSX.Element;
    label: string;
    onExecute?: (item: any) => void;
    route: string;
  }[];
  loading?: boolean;
  onApprove?: (item: any) => void;
  onChangePage?: (event: unknown, page: number) => void;
  onChangeRows?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove?: (item: any) => void;
  onSearch?: (query: string) => void;
  onSelect?: (item: any) => void;
  page?: number;
  rowsPerPage?: number;
  size?: 'small' | 'medium';
  striped?: boolean;
  title?: string;
  viewRoute?: string;
  editOnClick?: any;
};

type TableState = {
  page: number;
  approvalDialogItem: any;
  removalDialogItem: any;
  rowsPerPage: number;
};

export default function Table({
  addRoute,
  canAdd,
  canApprove,
  canEdit,
  canRemove,
  canSearch,
  canSelect,
  canView,
  columns,
  count,
  data,
  editRoute,
  elevation = 1,
  extraActions,
  loading,
  onApprove = emptyFunction,
  onChangePage = emptyFunction,
  onChangeRows = emptyFunction,
  onRemove = emptyFunction,
  onSearch = emptyFunction,
  onSelect = emptyFunction,
  page,
  rowsPerPage,
  size = 'medium',
  striped = true,
  title,
  viewRoute,
  editOnClick,
}: TableProps): JSX.Element {
  const classes = useStyles();
  const { push } = useHistory();
  const [state, setState] = useState<TableState>({
    page: 0,
    approvalDialogItem: undefined,
    removalDialogItem: undefined,
    rowsPerPage: 10,
  });
  const [selected, setSelected] = useState<any>([]);

  useEffect(() => {
    if (selected.length) {
      setSelected([]);
    }
  }, [data]);

  function handlePageChange(event: unknown, newPage: number): void {
    // Atualiza o estado local se não estiver controlado externamente
    if (page === undefined) {
      setState(prev => ({
        ...prev,
        page: newPage,
      }));
    }
    // Chama a função de callback passada via props
    if (onChangePage) {
      onChangePage(event, newPage);
    }
  }

  function handleRowsPerPageChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const newRowsPerPage = +event.target.value;
    
    // Atualiza o estado local se não estiver controlado externamente
    if (rowsPerPage === undefined) {
      setState(prev => ({
        ...prev,
        rowsPerPage: newRowsPerPage,
        page: 0, // Reset para a primeira página ao mudar o número de itens por página
      }));
    }
    
    // Chama as callbacks
    if (onChangeRows) {
      onChangeRows(event);
    }
    if (onChangePage) {
      onChangePage(event, 0);
    }
  }

  function handleApprove(item: any): void {
    setState({
      ...state,
      approvalDialogItem: item,
    });
  }

  function handleApprovalConfirm(): void {
    onApprove(state.approvalDialogItem);
    setState({
      ...state,
      approvalDialogItem: undefined,
    });
  }

  function handleApprovalDialogClose(): void {
    setState({
      ...state,
      approvalDialogItem: undefined,
    });
  }

  function savePatientLocalStorage(patient: any) {
    localStorage.setItem(KeysLocalStorage.DATA_PATIENT, JSON.stringify(patient));
  }  

  function handleRemove(item: any): void {
    savePatientLocalStorage(item);
    setState({
      ...state,
      removalDialogItem: item,
    });
  }

  function handleRemovalConfirm(): void {
    onRemove(state.removalDialogItem);
    setState({
      ...state,
      removalDialogItem: undefined,
    });
  }

  function handleRemovalDialogClose(): void {
    setState({
      ...state,
      removalDialogItem: undefined,
    });
  }

  function handleSelect(item: any): any {
    return function handleItemSelect(): void {
      const index = selected.indexOf(item.id);
      if (index >= 0) {
        const newSelected = [...selected];
        newSelected.splice(index, 1);
        setSelected(newSelected);
      } else {
        setSelected([...selected, item.id]);
      }
      onSelect(item);
    };
  }

  function handleClick(item: any) {
    return function handleItemClick(): void {
      if (!viewRoute) return;
      push(viewRoute.replace(':id', item.id));
    };
  }

  function renderHead(): JSX.Element[] {
    let cells: JSX.Element[] = [];
    if (canSelect) {
      cells = [<TableCell key="checkbox" />];
    }
    cells = [
      ...cells,
      ...columns.map((c, index) => (
        <TableCell
          key={`${c.key}[${index}]`}
          align={c.numeric || c.currency ? 'right' : 'left'}
          className={c.avatar ? classes.avatarCell : ''}
          style={c.width && c.maxWidth
            ? { width: c.width, maxWidth: c.maxWidth }
            : c.width
              ? { width: c.width }
              : c.maxWidth
                ? { maxWidth: c.maxWidth }
                : {}}
        >
          <strong>{c.label}</strong>
        </TableCell>
      )),
    ];
    if (canEdit || canRemove || canApprove || extraActions) {
      cells = [
        ...cells,
        <TableCell key="actions">
          <strong>Ações</strong>
        </TableCell>,
      ];
    }
    return cells;
  }

  function renderActions(item: any): JSX.Element[] {
    const actions: JSX.Element[] = [];
    if (canApprove && onApprove) {
      actions.push(
        <IconButton
          key={`tablerow[${item.id}][action][approve]`}
          aria-label="approve"
          onClick={(): void => handleApprove(item)}
          style={{
            maxHeight: 28,
            maxWidth: 28,
          }}
        >
          <Check />
        </IconButton>,
      );
    }
    if (canEdit && editRoute) {
      actions.push(
        <IconButton
          key={`tablerow[${item.id}][action][edit]`}
          aria-label="edit"
          component={Link}
          to={editRoute.replace(':id', item.id)}
          onClick={() => editOnClick? editOnClick(item) : undefined}
        >
          <Edit />
        </IconButton>,
      );
    }
    if (canRemove && onRemove) {
      actions.push(
        <IconButton
          key={`tablerow[${item.id}][action][remove]`}
          aria-label="remove"
          onClick={(): void => handleRemove(item)}
        >
          <DeleteIcon />
        </IconButton>,
      );
    }
    if (extraActions && extraActions.length) {
      extraActions.forEach((e) => actions.push(
        e.route ? (
          <IconButton
            key={`tablerow[${item.id}][action][${e.label}]`}
            aria-label={e.label}
            component={Link}
            to={e.route.replace(':id', item.id)}
          >
            {e.icon}
          </IconButton>
        ) : (
          <IconButton
            key={`tablerow[${item.id}][action][${e.label}]`}
            aria-label={e.label}
            onClick={(): void => e.onExecute && e.onExecute(item)}
          >
            {e.icon}
          </IconButton>
        ),
      ));
    }
    return actions;
  }

  function renderBody(): JSX.Element[] {
    const rows = count
      ? data
      : data.slice(
        state.page * state.rowsPerPage,
        state.page * state.rowsPerPage + state.rowsPerPage,
      );
    return rows.map((d: any) => (
      <TableRow
        hover={canSelect || canView}
        key={`tablerow[${d.id}]`}
        className={clsx({
          [classes.striped]: striped,
          [classes.selectableRow]: canSelect || canView,
        })}
        onClick={
          canApprove
            ? undefined
            : canView
              ? handleClick(d)
              : canSelect
                ? handleSelect(d)
                : undefined
        }
      >
        {canSelect && (
          <TableCell key={`tablerow[${d.id}][check]`} align="center" padding="checkbox">
            <Checkbox
              checked={selected.includes(d.id)}
              onClick={canView ? handleSelect(d) : undefined}
              icon={<CheckboxBlankCircleOutline />}
              checkedIcon={<CheckboxMarkedCircle />}
            />
          </TableCell>
        )}
        {columns.map((c: TableColumn, index: number) => (
          <TableCell
            key={`tablerow[${d.id}][${c.key}][${index}]`}
            className={clsx({ [classes.avatarCell]: c.avatar })}
            style={c.width && c.maxWidth
              ? { width: c.width, maxWidth: c.maxWidth }
              : c.width
                ? { width: c.width }
                : c.maxWidth
                  ? { maxWidth: c.maxWidth }
                  : {}}
            onClick={
              canApprove
                ? canView
                  ? handleClick(d)
                  : canSelect
                    ? handleSelect(d)
                    : undefined
                : undefined
            }
          >
            {c.avatar ? (
              <Avatar src={d[c.key]}>
                {!d[c.key] && c.avatarFallback !== undefined && d[c.avatarFallback]
                  ? d[c.avatarFallback].substring(0, 2).toUpperCase()
                  : ''}
              </Avatar>
            ) : c.render ? (
              c.render(d[c.key], d)
            ) : (
              d[c.key]
            )}
          </TableCell>
        ))}
        {((canEdit && editRoute)
          || (canRemove && onRemove)
          || (canApprove && onApprove)
          || extraActions) && (
          <TableCell key={`tablerow[${d.id}][actions]`} align="left">
            {renderActions(d)}
          </TableCell>
        )}
      </TableRow>
    ));
  }

  const { approvalDialogItem, removalDialogItem } = state;
  return (
    <Paper className={classes.container} elevation={elevation}>
      <PaperTitle title={title || ''}>
        {canSearch && (
          <Grid item>
            <SearchBar onChange={onSearch} label={'Pesquisar'} />
          </Grid>
        )}
        {canAdd && addRoute && (
          <Grid item>
            <Button
              component={Link}
              variant="contained"
              color="primary"
              startIcon={<AddBox />}
              to={addRoute}
            >
              Incluir
            </Button>
          </Grid>
        )}
      </PaperTitle>
      <TableContainer>
        <TableComponent size={size}>
          <TableHead>
            <TableRow>{renderHead()}</TableRow>
          </TableHead>
          <TableBody>{renderBody()}</TableBody>
        </TableComponent>
        <TablePagination
          component="div"
          page={page ?? state.page}
          rowsPerPage={rowsPerPage ?? state.rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          count={count ?? data.length}
          colSpan={columns.length}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </TableContainer>
      <Dialog
        open={!!approvalDialogItem}
        onClose={handleApprovalDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-approval">{'Confirmar aprovação'}</DialogTitle>
        <DialogActions>
          <Button onClick={handleApprovalDialogClose} color="primary">
            {'Cancelar'}
          </Button>
          <Button onClick={handleApprovalConfirm} color="primary" autoFocus>
            {'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!removalDialogItem}
        onClose={handleRemovalDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Confirmar exclusão'}</DialogTitle>
        <DialogActions>
          <Button onClick={handleRemovalDialogClose} color="primary">
           {'Cancelar'}
          </Button>
          <Button onClick={handleRemovalConfirm} color="primary" autoFocus>
           {'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
